import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import type { MortuaryBoard, MortuaryOccupant } from '@wodi/shared';
import {
  Button,
  CardGrid,
  EmptyState,
  LoadingBlock,
  Notice,
  PageHeader,
  Section,
  useShortcut,
} from '@wodi/ui';

import { mortuaryBoardQuery } from '../api/mortuary.ts';
import { can, sessionQuery } from '../api/session.ts';
import { useOnline } from '../hooks/use-online.ts';
import { ShortcutHint } from '../layout/screen.tsx';
import { formatInstant } from '../patients/format.ts';
import styles from '../vital-events/vital-events.module.css';
import { errorText, PLACE_WORDS } from '../vital-events/words.ts';

// The mortuary (docs/plans/phase2-mortuary-and-notifications.md §6): cold-room slots as tiles
// like the bed board, deaths waiting to be received, and releases waiting for approval. A
// body on a record the reader may not see shows as "Restricted record".

type Slot = MortuaryBoard['coldRooms'][number]['slots'][number];

const STATE_WORDS: Record<Slot['state'], string> = {
  occupied: 'Occupied',
  free: 'Free',
  out_of_service: 'Out of service',
};

function daysText(days: number): string {
  return days === 1 ? 'Day 1' : `Day ${String(days)}`;
}

function OccupantLines({ occupant }: { occupant: MortuaryOccupant }) {
  return (
    <>
      <Link to="/mortuary/cases/$caseId" params={{ caseId: occupant.caseId }} className="wodi-id">
        {occupant.caseNumber}
      </Link>
      <span className={styles.slotName}>
        {occupant.patient === null ? 'Restricted record' : occupant.patient.displayName}
      </span>
      <span className={styles.slotDays}>{daysText(occupant.daysInStorage)}</span>
    </>
  );
}

function SlotTile({ slot }: { slot: Slot }) {
  return (
    <li
      className={styles.slot}
      data-state={slot.state}
      aria-label={`Slot ${slot.label}, ${STATE_WORDS[slot.state]}`}
    >
      <span className={styles.slotLabel}>{slot.label}</span>
      <span className={styles.slotState}>{STATE_WORDS[slot.state]}</span>
      {slot.occupant === null ? null : <OccupantLines occupant={slot.occupant} />}
      {slot.state === 'out_of_service' && slot.statusReason !== null ? (
        <span className={styles.slotDays}>{slot.statusReason}</span>
      ) : null}
    </li>
  );
}

function Board({
  board,
  mayIntake,
  mayReadDeaths,
}: {
  board: MortuaryBoard;
  mayIntake: boolean;
  /** Causes of death can reveal sensitive conditions: attendants don't open death records. */
  mayReadDeaths: boolean;
}) {
  const bodies =
    board.unslotted.length +
    board.coldRooms.reduce((n, r) => n + r.slots.filter((s) => s.occupant !== null).length, 0);
  return (
    <>
      <Section
        title="Cold rooms"
        description={`${String(bodies)} ${bodies === 1 ? 'body' : 'bodies'} in storage`}
      >
        {board.coldRooms.length === 0 ? (
          <EmptyState>No cold rooms set up yet. Add slots in Settings &gt; Mortuary.</EmptyState>
        ) : (
          board.coldRooms.map((room) => (
            <div key={room.name} className={styles.room}>
              <h3 className={styles.roomName}>{room.name}</h3>
              <ul className={styles.grid} aria-label={`Slots in ${room.name}`}>
                {room.slots.map((s) => (
                  <SlotTile key={s.id} slot={s} />
                ))}
              </ul>
            </div>
          ))
        )}
        {board.unslotted.length === 0 ? null : (
          <div className={styles.room}>
            <h3 className={styles.roomName}>Not in a slot yet</h3>
            <ul className={styles.list} aria-label="Not in a slot yet">
              {board.unslotted.map((o) => (
                <li key={o.caseId} className={styles.listRow}>
                  <OccupantLines occupant={o} />
                </li>
              ))}
            </ul>
          </div>
        )}
      </Section>
      <CardGrid>
        <Section title="Waiting to be received">
          {board.awaitingIntake.length === 0 ? (
            <EmptyState>
              No deaths waiting. Deaths recorded here appear until the body is received.
            </EmptyState>
          ) : (
            <ul className={styles.list} aria-label="Waiting to be received">
              {board.awaitingIntake.map((d) => (
                <li key={d.deathId} className={styles.listRow}>
                  <span>
                    {mayReadDeaths ? (
                      <Link to="/deaths/$deathId" params={{ deathId: d.deathId }}>
                        {d.patient.displayName}
                      </Link>
                    ) : (
                      d.patient.displayName
                    )}{' '}
                    <span className="wodi-id">{d.patient.patientNumber}</span>
                    <span className={styles.meta}>
                      {` ${PLACE_WORDS[d.place]}, ${formatInstant(d.diedAt)}`}
                    </span>
                  </span>
                  {mayIntake ? (
                    <Link to="/mortuary/receive" search={{ deathId: d.deathId }}>
                      Receive body
                    </Link>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </Section>
        <Section title="Waiting for release approval">
          {board.awaitingRelease.length === 0 ? (
            <EmptyState>No releases waiting for approval.</EmptyState>
          ) : (
            <ul className={styles.list} aria-label="Waiting for release approval">
              {board.awaitingRelease.map((r) => (
                <li key={r.requestId} className={styles.listRow}>
                  <Link to="/mortuary/cases/$caseId" params={{ caseId: r.caseId }}>
                    {`${r.caseNumber} ${r.patient === null ? 'Restricted record' : r.patient.displayName}`}
                  </Link>
                  <span className={styles.meta}>{`Asked ${formatInstant(r.requestedAt)}`}</span>
                </li>
              ))}
            </ul>
          )}
        </Section>
      </CardGrid>
    </>
  );
}

export function MortuaryPage() {
  const { data: session } = useSuspenseQuery(sessionQuery);
  const board = useQuery(mortuaryBoardQuery);
  const online = useOnline();
  const navigate = useNavigate();
  const mayIntake = session !== null && can(session, 'mortuary.intake');
  const receive = () => {
    void navigate({ to: '/mortuary/receive', search: {} });
  };
  useShortcut(
    'r',
    () => {
      if (mayIntake && online) receive();
    },
    { enabled: mayIntake, description: 'Receive a body' },
  );
  if (session === null || !can(session, 'mortuary.read')) {
    return (
      <>
        <PageHeader title="Mortuary" />
        <Notice tone="info">
          You don&apos;t have permission to see the mortuary. Ask an administrator if you need it.
        </Notice>
      </>
    );
  }
  return (
    <div className={styles.page}>
      <PageHeader
        title="Mortuary"
        description="Bodies in storage, deaths waiting to be received, and releases waiting for approval."
        actions={
          mayIntake ? (
            <Button variant="primary" isDisabled={!online} onPress={receive} shortcut="r">
              Receive body
            </Button>
          ) : null
        }
      />
      {mayIntake ? <ShortcutHint combo="r" action="receive a body" /> : null}
      {!online ? (
        <Notice tone="info">
          The mortuary needs a connection to Wodi. Reconnect to see the cold rooms and receive
          bodies.
        </Notice>
      ) : null}
      {board.isPending ? <LoadingBlock label="Loading the mortuary" rows={6} /> : null}
      {board.isError ? (
        <Notice
          tone="error"
          action={<Button onPress={() => void board.refetch()}>Try again</Button>}
        >
          {errorText(board.error, 'The mortuary could not be loaded. Try again.')}
        </Notice>
      ) : null}
      {board.isSuccess && !board.data.setUp ? (
        <Notice tone="info">
          The mortuary is not set up here yet. An administrator chooses the daily storage charge in
          Settings &gt; Mortuary.
        </Notice>
      ) : null}
      {board.isSuccess ? (
        <Board
          board={board.data}
          mayIntake={mayIntake}
          mayReadDeaths={can(session, 'deaths.read')}
        />
      ) : null}
    </div>
  );
}
